import { memo } from 'react';

import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { type WorkspaceAgentPhaseListItem } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/types';
import { WorkspaceAgentPhaseStatusIcon } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/WorkspaceAgentPhaseStatusIcon';

const workspaceAgentPhaseList = tailwindVariants({
  variants: {
    variant: {
      // Sits inside a loader's indent while the agent works, so it stays unboxed and recedes.
      steps: 'space-y-1.5',
      // The settled recap of what was done. Boxed, because by now it is a section of a result
      // rather than a live readout.
      panel: 'space-y-2.5 rounded-8 bg-gray-2/60 px-3 py-2.5',
    },
  },
});

export type WorkspaceAgentPhaseListVariant = NonNullable<VariantProps<typeof workspaceAgentPhaseList>['variant']>;

export type WorkspaceAgentPhaseListProps = {
  'data-testid'?: string;
  /**
   * Applied to every row, so a test can query the set without a value threaded through each item.
   * Matches every row rather than one, so query all-matching.
   */
  itemTestId?: string;
  phases: WorkspaceAgentPhaseListItem[];
  variant: WorkspaceAgentPhaseListVariant;
};

/**
 * The agent's work, one row per phase.
 *
 * Both variants render the same rows and strike through completed phases. They differ in layout:
 * `steps` is the live readout while the run is in flight; `panel` is the settled recap and allows
 * a second detail line.
 */
export const WorkspaceAgentPhaseList = memo(
  ({ 'data-testid': testId, itemTestId, phases, variant }: WorkspaceAgentPhaseListProps) => {
    const isPanel = variant === 'panel';

    return (
      <ul className={workspaceAgentPhaseList({ variant })} data-testid={testId}>
        {phases.map((phase) => (
          <li
            className={cn(
              'flex gap-1.5 text-12 leading-4',
              // A detail line makes the row taller than its glyph, so the glyph pins to the first line.
              isPanel ? 'items-start gap-2 text-gray-11' : 'items-center text-gray-10',
            )}
            data-testid={itemTestId}
            key={phase.id}
          >
            <WorkspaceAgentPhaseStatusIcon status={phase.status} />
            {isPanel ? (
              <div className="min-w-0">
                <p className={cn('font-medium', phase.status === 'done' && 'text-gray-9 line-through')}>
                  {phase.label}
                </p>
                {phase.detail ? <p className="mt-0.5 break-words text-gray-9">{phase.detail}</p> : null}
              </div>
            ) : (
              <span className={cn('truncate', phase.status === 'done' && 'text-gray-9 line-through')}>
                {phase.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  },
);

WorkspaceAgentPhaseList.displayName = 'WorkspaceAgentPhaseList';
